import React, { useState, useEffect } from 'react';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  ReceiptPercentIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { useAnalytics } from '../hooks/useAnalytics';

const ExpenseModal = ({ expense, categories, onClose, onSave }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [signedReceiptUrl, setSignedReceiptUrl] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingReceipt, setProcessingReceipt] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category_id: '',
    amount: '',
    receipt_url: null,
    notes: '',
    vendor: '',
    invoice_number: '',
    ...expense,
  });

  useEffect(() => {
    checkSubscription();
  }, []);

  const getSignedUrl = async () => {
    try {
      if (!formData.receipt_url) return;
      
      // Extract file path from GCP URL
      const matches = formData.receipt_url.match(/storage\.googleapis\.com\/[^/]+\/(.+)/);
      if (!matches) return;

      const [_, path] = matches;
      
      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      // Get signed URL from our edge function
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_FUNCTIONS_URL}/upload-to-gcp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          fileName: path,
          operation: 'getSignedUrl',
          supabase_url: process.env.REACT_APP_SUPABASE_URL,
          supabase_key: process.env.REACT_APP_SUPABASE_ANON_KEY
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get signed URL');
      
      if (data?.signedUrl) {
        console.log('Successfully got signed URL');
        setSignedReceiptUrl(data.signedUrl);
      }
    } catch (error) {
      console.error('Error getting signed URL:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('subscriptions')
        .select('subscription_type')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setIsSubscribed(profile?.subscription_type.toLowerCase() === 'paid' || false);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleFileChange = async (e) => {
    try {
      setLoading(true);
      const file = e.target.files[0];
      if (!file) return;

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        try {
          const base64Image = reader.result.split(',')[1];
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random()}.${fileExt}`;

          // Get session for authentication
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('No session found');

          // Call the upload-to-gcp edge function
          const response = await fetch(`${process.env.REACT_APP_SUPABASE_FUNCTIONS_URL}/upload-to-gcp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              base64Image,
              fileName,
              contentType: file.type,
              operation: 'upload',
              supabase_url: process.env.REACT_APP_SUPABASE_URL,
              supabase_key: process.env.REACT_APP_SUPABASE_ANON_KEY
            }),
          });

          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Failed to upload receipt');

          setFormData(prev => ({
            ...prev,
            receipt_url: data.url
          }));

          // Handle AI processing results if available
          if (data.aiProcessing) {
            if (data.aiProcessing.processing_error) {
              alert('Receipt uploaded but could not be processed automatically. Please enter details manually.');
            } else {
              // Update form fields with AI extracted data
              if (data.aiProcessing.amount) {
                const parsedAmount = typeof data.aiProcessing.amount === 'string'
                  ? parseFloat(data.aiProcessing.amount.replace(/[^0-9.]/g, ''))
                  : data.aiProcessing.amount;
                
                if (!isNaN(parsedAmount)) {
                  setFormData(prev => ({ ...prev, amount: parsedAmount.toString() }));
                }
              }

              if (data.aiProcessing.vendor) setFormData(prev => ({ ...prev, vendor: data.aiProcessing.vendor }));
              if (data.aiProcessing.invoice_number) setFormData(prev => ({ ...prev, invoice_number: data.aiProcessing.invoice_number }));
              if (data.aiProcessing.date) setFormData(prev => ({ ...prev, date: data.aiProcessing.date }));
              if (data.aiProcessing.notes) setFormData(prev => ({ ...prev, notes: data.aiProcessing.notes }));

              if (data.aiProcessing.suggested_category) {
                const proceed = window.confirm(
                  `AI suggests categorizing this as "${data.aiProcessing.suggested_category}". Would you like to use this category?`
                );

                if (proceed) {
                  // Check if category exists
                  const existingCategory = categories.find(
                    cat => cat.name.toLowerCase() === data.aiProcessing.suggested_category.toLowerCase()
                  );

                  if (existingCategory) {
                    setFormData(prev => ({ ...prev, category_id: existingCategory.category_id }));
                  } else {
                    try {
                      const { data: newCategory, error } = await supabase
                        .from('categories')
                        .insert([{ name: data.aiProcessing.suggested_category, user_id: user.id }])
                        .select()
                        .single();

                      if (error) throw error;
                      
                      // Refresh categories list
                      const { data: updatedCategories } = await supabase
                        .from('categories')
                        .select('*')
                        .eq('user_id', user.id);

                      if (newCategory) {
                        setFormData(prev => ({ ...prev, category_id: newCategory.category_id }));
                      }
                    } catch (error) {
                      console.error('Error creating category:', error);
                      alert('Failed to create category');
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error uploading receipt:', error);
          setError('Failed to upload receipt');
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        console.error('Error reading file');
        setError('Failed to read file');
        setLoading(false);
      };
    } catch (error) {
      console.error('Error handling file:', error);
      setError('Failed to handle file');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (expense?.expense_id) {
        // Update existing expense
        const { error } = await supabase
          .from('expenses')
          .update(formData)
          .eq('expense_id', expense.expense_id);

        if (error) throw error;
      } else {
        // Create new expense
        const { error } = await supabase
          .from('expenses')
          .insert([{ ...formData, user_id: user.id }]);

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Error saving expense:', error);
      setError('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6" style={{ boxShadow: shadows.xl }}>
        <h2 className="text-xl font-bold mb-4" style={{ color: THEME.text }}>
          {expense ? 'Edit Expense' : 'New Expense'}
        </h2>

        {error && (
          <div 
            className="rounded-lg p-4 mb-4 text-sm"
            style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Category
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            >
              <option value="">Select a category</option>
              {categories.sort((a, b) => a.name.localeCompare(b.name)).map((category) => (
                <option key={category.category_id} value={category.category_id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Vendor
            </label>
            <input
              type="text"
              name="vendor"
              value={formData.vendor}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Invoice Number
            </label>
            <input
              type="text"
              name="invoice_number"
              value={formData.invoice_number}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Amount
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
              min="0.01"
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
              Receipt
            </label>
            <div className="flex items-center space-x-4">
              {formData.receipt_url ? (
                <a
                  href={signedReceiptUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm"
                  style={{ color: THEME.primary }}
                  onClick={(e) => {
                    if (!signedReceiptUrl) {
                      e.preventDefault();
                      getSignedUrl(formData.receipt_url);
                    }
                  }}
                >
                  <ReceiptPercentIcon className="w-5 h-5 mr-1" />
                  View Receipt
                </a>
              ) : (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${THEME.primary}15` }}
                >
                  <ReceiptPercentIcon className="w-6 h-6" style={{ color: THEME.primary }} />
                </div>
              )}
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
                id="receipt-upload"
                disabled={loading || processingReceipt}
              />
              <label
                htmlFor="receipt-upload"
                className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer flex items-center ${(loading || processingReceipt) ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ 
                  backgroundColor: `${THEME.primary}15`,
                  color: THEME.primary
                }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent mr-2" 
                      style={{ borderColor: `${THEME.primary} transparent` }} />
                    Uploading...
                  </>
                ) : processingReceipt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent mr-2" 
                      style={{ borderColor: `${THEME.primary} transparent` }} />
                    Processing with AI...
                  </>
                ) : (
                  <>
                    {isSubscribed ? 'Process with AI' : 'Upload Receipt'}
                    {isSubscribed && <span className="ml-1 text-xs">(Premium)</span>}
                  </>
                )}
              </label>
            </div>
            {processingReceipt && (
              <p className="mt-2 text-sm" style={{ color: THEME.subtext }}>
                AI is analyzing your receipt. The form will be pre-filled once processing is complete.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={2}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              placeholder="Add any notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ color: THEME.text }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
              style={{ 
                backgroundColor: THEME.primary,
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Expenses() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [error, setError] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState({
    category_id: '',
    startDate: '',
    endDate: '',
  });
  const { logEvent } = useAnalytics();

  useEffect(() => {
    logEvent('Expenses', 'View Expenses', 'Page Load');
    fetchCategories();
    fetchExpenses();
  }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', user.id);

    if (error) throw error;
    setCategories(data || []);
  };  

  const fetchExpenses = async () => {
    try {
      let query = supabase
        .from('expenses')
        .select('*, categories(*)')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (filter.category_id) {
        query = query.eq('category_id', filter.category_id);
      }
      if (filter.startDate) {
        query = query.gte('date', filter.startDate);
      }
      if (filter.endDate) {
        query = query.lte('date', filter.endDate);
      }

      const { data, error } = await query;
      

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      logEvent('Expenses', 'Delete Expense', `Expense ID: ${expenseId}`);
      
      // First get the expense to get the receipt URL
      const { data: expense, error: fetchError } = await supabase
        .from('expenses')
        .select('receipt_url')
        .eq('expense_id', expenseId)
        .single();

      if (fetchError) throw fetchError;

      // Delete the expense from the database
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('expense_id', expenseId);

      if (error) throw error;

      // If there was a receipt, delete it from storage
      if (expense?.receipt_url) {
        await deleteReceipt(expense.receipt_url);
      }

      await fetchExpenses();
      logEvent('Expenses', 'Delete Expense Success', `Expense ID: ${expenseId}`);
    } catch (error) {
      console.error('Error deleting expense:', error);
      setError('Failed to delete expense');
      logEvent('Expenses', 'Delete Expense Error', error.message);
    }
  };


  const deleteReceipt = async (receiptUrl) => {
    try {
      if (!receiptUrl) return;

      // Extract file path from GCP URL
      const matches = receiptUrl.match(/storage\.googleapis\.com\/[^/]+\/(.+)/);
      if (!matches) return;

      const [_, path] = matches;

      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      // Delete file using edge function
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_FUNCTIONS_URL}/upload-to-gcp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          fileName: path,
          operation: 'delete',
          supabase_url: process.env.REACT_APP_SUPABASE_URL,
          supabase_key: process.env.REACT_APP_SUPABASE_ANON_KEY
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete file');
      
      console.log('Successfully deleted receipt from storage');
    } catch (error) {
      console.error('Error deleting receipt:', error);
    }
  };


  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedExpense(null);
    setShowModal(false);
  };

  const handleSave = async () => {
    await fetchExpenses();
    handleCloseModal();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: value
    }));
    logEvent('Expenses', 'Filter Change', filter);
  };

  const handleApplyFilter = () => {
    fetchExpenses();
  };

  const handleClearFilter = () => {
    setFilter({
      category: '',
      startDate: '',
      endDate: '',
    });
    fetchExpenses();
  };

  const calculateTotal = () => {
    return expenses.reduce((sum, expense) => sum + parseFloat(expense.amount), 0);
  };

  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>Expenses</h1>
          <p style={{ color: THEME.subtext }}>Track and manage your business expenses</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
          style={{ 
            background: THEME.primary,
            boxShadow: shadows.md,
          }}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Expense
        </button>
      </div>

      {error && (
        <div 
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
        >
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl p-6 mb-6" style={{ boxShadow: shadows.md }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Category
            </label>
            <select
              name="category_id"
              value={filter.category_id}
              onChange={handleFilterChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            >
              <option value="">All Categories</option>
              {sortedCategories.map((category) => (
                <option key={category.category_id} value={category.category_id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filter.startDate}
              onChange={handleFilterChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filter.endDate}
              onChange={handleFilterChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            />
          </div>

          <div className="flex items-end space-x-2">
            <button
              onClick={handleApplyFilter}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ backgroundColor: THEME.primary }}
            >
              Apply Filter
            </button>
            <button
              onClick={handleClearFilter}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ color: THEME.text }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl" style={{ boxShadow: shadows.md }}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.expense_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: THEME.text }}>
                      {new Date(expense.date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FolderIcon className="h-5 w-5 mr-2" style={{ color: THEME.primary }} />
                      <span style={{ color: THEME.text }}>{expense.categories?.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {expense.notes && (
                      <div 
                        className="text-sm w-48 lg:w-64 truncate" 
                        style={{ color: THEME.subtext }}
                        title={expense.notes}
                      >
                        {expense.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium" style={{ color: THEME.text }}>
                      ${parseFloat(expense.amount).toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: THEME.text }}>
                      {expense.invoice_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {expense.receipt_url && (
                      <ReceiptViewer url={expense.receipt_url} />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        onClick={() => handleEdit(expense)}
                        className="text-gray-400 hover:text-gray-500"
                        title="Edit Expense"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.expense_id)}
                        className="text-gray-400 hover:text-red-500"
                        title="Delete Expense"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center" style={{ color: THEME.subtext }}>
                    No expenses found. Add your first expense to get started!
                  </td>
                </tr>
              )}
            </tbody>
            {expenses.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={3} className="px-6 py-4 text-right font-medium" style={{ color: THEME.text }}>
                    Total:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium" style={{ color: THEME.primary }}>
                    ${calculateTotal().toFixed(2)}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {showModal && (
        <ExpenseModal
          expense={selectedExpense}
          categories={categories}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

const ReceiptViewer = ({ url }) => {
  const [signedUrl, setSignedUrl] = useState(null);

  const getSignedUrl = async () => {
    try {
      const matches = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)/);
      if (!matches) return;

      const [_, bucket, path] = matches;
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .createSignedUrl(path, 3600); // 1 hour expiry

      if (error) throw error;
      if (data?.signedUrl) {
        setSignedUrl(data.signedUrl);
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error getting signed URL:', error);
    }
  };

  return (
    <button
      onClick={getSignedUrl}
      className="text-sm"
      style={{ color: THEME.primary }}
    >
      <ReceiptPercentIcon className="h-5 w-5" />
    </button>
  );
}; 